/* eslint-disable react-refresh/only-export-components */
import { Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import LangLayout from './layouts/LangLayout'
import FeatureGate from './components/system/FeatureGate'
import type { FlagKey } from './config/flags'
import { DEFAULT_LANG } from './config/locales'
import RequireCustomer from './components/account/RequireCustomer'
import { chunkReloader } from './appRuntime'

const HomePage = lazy(chunkReloader.wrap(() => import('./pages/HomePage')))
const ServicesPage = lazy(chunkReloader.wrap(() => import('./pages/ServicesPage')))
const ServiceDetailPage = lazy(chunkReloader.wrap(() => import('./pages/ServiceDetailPage')))
const TrichologyPage = lazy(chunkReloader.wrap(() => import('./pages/TrichologyPage')))
const TrichologyDetailPage = lazy(chunkReloader.wrap(() => import('./pages/TrichologyDetailPage')))
const BranchesPage = lazy(chunkReloader.wrap(() => import('./pages/BranchesPage')))
const SalonDetailPage = lazy(chunkReloader.wrap(() => import('./pages/SalonDetailPage')))
const MembershipPage = lazy(chunkReloader.wrap(() => import('./pages/MembershipPage')))
const MySubscriptionPage = lazy(chunkReloader.wrap(() => import('./pages/MySubscriptionPage')))
const GiftCardsPage = lazy(chunkReloader.wrap(() => import('./pages/GiftCardsPage')))
const GiftGuidePage = lazy(chunkReloader.wrap(() => import('./pages/GiftGuidePage')))
const ShopPage = lazy(chunkReloader.wrap(() => import('./pages/ShopPage')))
const ProductPage = lazy(chunkReloader.wrap(() => import('./pages/ProductPage')))
const CheckoutPage = lazy(chunkReloader.wrap(() => import('./pages/CheckoutPage')))
const AboutPage = lazy(chunkReloader.wrap(() => import('./pages/AboutPage')))
const CareersPage = lazy(chunkReloader.wrap(() => import('./pages/CareersPage')))
const ContactPage = lazy(chunkReloader.wrap(() => import('./pages/ContactPage')))
const HairLengthGuidePage = lazy(chunkReloader.wrap(() => import('./pages/HairLengthGuidePage')))
const FaqPage = lazy(chunkReloader.wrap(() => import('./pages/FaqPage')))
const CancellationPolicyPage = lazy(chunkReloader.wrap(() => import('./pages/CancellationPolicyPage')))
const RefundPolicyPage = lazy(chunkReloader.wrap(() => import('./pages/RefundPolicyPage')))
const TermsPage = lazy(chunkReloader.wrap(() => import('./pages/TermsPage')))
const PrivacyPage = lazy(chunkReloader.wrap(() => import('./pages/PrivacyPage')))
const CookiesPage = lazy(chunkReloader.wrap(() => import('./pages/CookiesPage')))
const PhotoConsentPage = lazy(chunkReloader.wrap(() => import('./pages/PhotoConsentPage')))
const ConsentWithdrawPage = lazy(chunkReloader.wrap(() => import('./pages/ConsentWithdrawPage')))
const SuccessPage = lazy(chunkReloader.wrap(() => import('./pages/SuccessPage')))
const OrderConfirmedPage = lazy(chunkReloader.wrap(() => import('./pages/OrderConfirmedPage')))
const OrderPage = lazy(chunkReloader.wrap(() => import('./pages/OrderPage')))
const AccountPage = lazy(chunkReloader.wrap(() => import('./pages/AccountPage')))
const AccountOrdersPage = lazy(chunkReloader.wrap(() => import('./pages/AccountOrdersPage')))
const AccountOrderPage = lazy(chunkReloader.wrap(() => import('./pages/AccountOrderPage')))
const NotFoundPage = lazy(chunkReloader.wrap(() => import('./pages/NotFoundPage')))

function LazyLoad({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      }
    >
      {children}
    </Suspense>
  )
}

/** Wraps a page in feature-flag gating + lazy boundary. */
function Gated({ flag, children }: { flag: FlagKey; children: React.ReactNode }) {
  return (
    <FeatureGate flag={flag}>
      <LazyLoad>{children}</LazyLoad>
    </FeatureGate>
  )
}

// Exported so tests can mount the same routes via createMemoryRouter without
// pulling in BrowserRouter / window history.
export const routes = [
  {
    path: '/:lang',
    element: <LangLayout />,
    children: [
      { index: true, element: <Gated flag="HOME"><HomePage /></Gated> },
      { path: 'services', element: <Gated flag="SERVICES"><ServicesPage /></Gated> },
      { path: 'services/:slug', element: <Gated flag="SERVICES"><ServiceDetailPage /></Gated> },
      { path: 'trichology', element: <Gated flag="TRICHOLOGY"><TrichologyPage /></Gated> },
      { path: 'trichology/:slug', element: <Gated flag="TRICHOLOGY"><TrichologyDetailPage /></Gated> },
      { path: 'branches', element: <Gated flag="BRANCHES"><BranchesPage /></Gated> },
      { path: 'salon/:id', element: <Gated flag="BRANCHES"><SalonDetailPage /></Gated> },
      { path: 'membership', element: <Gated flag="MEMBERSHIP"><MembershipPage /></Gated> },
      { path: 'membership/my-subscription', element: <Gated flag="MEMBERSHIP"><MySubscriptionPage /></Gated> },
      { path: 'gift-cards', element: <Gated flag="GIFT_CARDS"><GiftCardsPage /></Gated> },
      { path: 'gift-guide', element: <Gated flag="GIFT_GUIDE"><GiftGuidePage /></Gated> },
      { path: 'shop', element: <Gated flag="SHOP"><ShopPage /></Gated> },
      // Static paths before :handle so they are never shadowed by the dynamic segment.
      { path: 'shop/checkout', element: <Gated flag="SHOP"><CheckoutPage /></Gated> },
      // Not feature-gated: must always resolve so a Stripe Checkout redirect never 404s.
      { path: 'shop/order-confirmed', element: <LazyLoad><OrderConfirmedPage /></LazyLoad> },
      // The order page: the payment-failed e-mail links here in every language, and
      // Stripe returns here (?payment=success|cancelled) after "pay again". Not
      // feature-gated, for the same reason as order-confirmed.
      { path: 'orders/:id', element: <LazyLoad><OrderPage /></LazyLoad> },
      { path: 'shop/:handle', element: <Gated flag="SHOP"><ProductPage /></Gated> },
      // Customer portal: sign-in, then the signed-in customer's own orders.
      { path: 'account', element: <Gated flag="ACCOUNT"><AccountPage /></Gated> },
      { path: 'account/orders', element: <Gated flag="ACCOUNT"><RequireCustomer><AccountOrdersPage /></RequireCustomer></Gated> },
      { path: 'account/orders/:id', element: <Gated flag="ACCOUNT"><RequireCustomer><AccountOrderPage /></RequireCustomer></Gated> },
      { path: 'about', element: <Gated flag="ABOUT"><AboutPage /></Gated> },
      { path: 'careers', element: <Gated flag="CAREERS"><CareersPage /></Gated> },
      { path: 'contact', element: <Gated flag="CONTACT"><ContactPage /></Gated> },
      { path: 'hair-length-guide', element: <Gated flag="HAIR_LENGTH_GUIDE"><HairLengthGuidePage /></Gated> },
      { path: 'hair-guide', element: <Navigate to="../hair-length-guide" replace /> },
      { path: 'faq', element: <Gated flag="FAQ"><FaqPage /></Gated> },
      { path: 'cancellation-policy', element: <Gated flag="CANCELLATION_POLICY"><CancellationPolicyPage /></Gated> },
      { path: 'refund-policy', element: <Gated flag="REFUND_POLICY"><RefundPolicyPage /></Gated> },
      { path: 'terms', element: <Gated flag="TERMS"><TermsPage /></Gated> },
      { path: 'privacy', element: <Gated flag="PRIVACY"><PrivacyPage /></Gated> },
      { path: 'cookies', element: <Gated flag="COOKIES"><CookiesPage /></Gated> },
      { path: 'photo-consent', element: <Gated flag="PHOTO_CONSENT"><PhotoConsentPage /></Gated> },
      // Where the "withdraw my consent" link in the confirmation email lands.
      // Deliberately NOT <Gated>: switching the consent page off is a marketing
      // decision, and it must never strand the withdrawal links already sitting
      // in people's inboxes. GDPR Art. 7(3) is not a feature toggle.
      { path: 'photo-consent/withdraw', element: <ConsentWithdrawPage /> },
      // Short alias for the printed QR code and for anyone typing the URL.
      // The canonical path is /photo-consent: plain "consent" reads as COOKIE
      // consent both in this codebase and to a visitor scanning the legal menu.
      { path: 'consent', element: <Navigate to="../photo-consent" replace relative="path" /> },
      // Newsletter double-opt-in landing. NOT feature-gated: it's the Brevo
      // post-confirmation redirect target and must always resolve.
      { path: 'newsletter/confirmed', element: <LazyLoad><SuccessPage variant="newsletter" /></LazyLoad> },
      // Unknown paths and hidden pages (FeatureGate sends them to /:lang/__not-found) stay inside the
      // language layout, so a 404 keeps <html lang> and the copy of its language (review 2026-10-20).
      { path: '*', element: <LazyLoad><NotFoundPage /></LazyLoad> },
    ],
  },
  // Bare (non-locale-prefixed) Brevo redirect target. `/newsletter/confirmed`
  // is two segments, so it does NOT match `/:lang` (one segment) and would
  // otherwise fall through to the `*` NotFound. Redirect it to the default
  // locale so Brevo can point at the un-prefixed path.
  { path: '/newsletter/confirmed', element: <Navigate to={`/${DEFAULT_LANG}/newsletter/confirmed`} replace /> },
  { path: '/', element: <Navigate to={`/${DEFAULT_LANG}`} replace /> },
  { path: '*', element: <LazyLoad><NotFoundPage /></LazyLoad> },
]

// NOTE: the browser router instance (createBrowserRouter) is created in
// main.tsx, NOT here. Keeping this module free of createBrowserRouter lets the
// prerender SSR entry (entry-server.tsx) import `routes` without touching
// `document` (createBrowserHistory throws under Node). Tests build their own
// router from `routes` via createMemoryRouter.
